import { atom, type Getter, type Setter } from "jotai";
import { atomWithQuery } from "jotai/query";

import type {
  ComponentOverlayer,
  LayerGroup,
  Overlayer,
  BackendPredefinedLEZ,
  Emission,
  WMSOverlayer,
  PredefinedLEZ,
} from "src/types";
import { GROUPS, staticOverlayers } from "src/config/overlayers";
import {
  addOrUpdateInList,
  deserializeBackendLayer,
  extraLayerToOverlayer,
  filterActive,
  residentialMapToOverlayer,
  residentialTotalMapToOverlayer,
} from "src/utils/layer-manager.utils";
import { queryFn as queryResidentialMaps } from "src/hooks/useResidentialMaps";
import { baseDataKeys } from "src/query-keys";
import { fetchExtraLayers } from "src/hooks/useExtraLayers";
import { PredefinedLEZsLegend } from "src/components/legend-item/PredefinedLEZsLegend";
import wkx from "wkx";

import axios from "axios";
import type { MultiPolygon, Polygon } from "geojson";
import type { ExtraLayer } from "src/types/extra-layers";
import { PredefinedLEZsOnMap } from "src/components/on-map/PredefinedLEZsOnMap";
import type { Feature } from "geojson";

export const overlayersAtom = atom<Array<Overlayer | ComponentOverlayer>>((get: Getter) => {
  const activeOverlayers = get(activeOverlayersAtom);
  const scenarioEmissionLayers = get(scenarioEmissionsDerivedAtom);
  const residentialMaps = get(residentialMapsAtom);
  const extraLayers = get(extraLayersAtom);
  const atmoStreetLayers = get(atmoStreetLayersAtom);
  const stationLayers = get(stationLayersAtom);
  const predefinedLEZs = get(predefinedLEZsAtom);
  const allOverlayers = [
    ...staticOverlayers,
    ...predefinedLEZs,
    ...stationLayers,
    ...atmoStreetLayers,
    ...scenarioEmissionLayers,
    ...(residentialMaps ? residentialMaps : []),
    ...(extraLayers ? extraLayers : []),
  ];

  return allOverlayers.map((layer) => {
    const result = activeOverlayers.find((a) => a.key === layer.key);

    if (result) return result;
    return layer;
  });
});
export const selectedScenarioIdAtom = atom<string | undefined>(undefined);

export const scenarioEmissionsDerivedAtom = atom((get) => {
  const scenarioId = get(selectedScenarioIdAtom);
  if (!scenarioId) return [];

  return get(scenarioEmissionsAtom(scenarioId));
});

export const stationLayersAtom = atomWithQuery(() => ({
  queryKey: baseDataKeys.stations,
  queryFn: async () => {
    const { data } = await axios.get<ExtraLayer[]>("/planning-api/layers/stations");
    return data.map((l) =>
      deserializeBackendLayer(l, "stations", GROUPS.stations, ["case-create", "case-edit", "case-results"], l.unit)
    );
  },
}));

export const atmoStreetLayersAtom = atomWithQuery(() => ({
  queryKey: baseDataKeys.atmoStreetLayers,
  queryFn: async () => {
    const { data } = await axios.get<ExtraLayer[]>("/planning-api/layers/atmostreet");
    return data.map((l) =>
      deserializeBackendLayer(l, "atmostreet", GROUPS.atmostreet, ["case-create", "case-edit", "case-results"], l.unit)
    );
  },
}));

export const predefinedLEZsAtom = atomWithQuery(() => {
  return {
    queryKey: baseDataKeys.predefinedLEZs,
    queryFn: async () => {
      function deserialize(data: BackendPredefinedLEZ[]): PredefinedLEZ[] {
        return data.map((item) => ({
          ...item,
          geom: wkx.Geometry.parse(item.geom_frontendSrid).toGeoJSON() as Feature<Polygon | MultiPolygon>,
        }));
      }
      const { data } = await axios.get<BackendPredefinedLEZ[]>("/planning-api/genericzones");
      if (data.length === 0) return [];
      const predefinedLEZsLayer: Overlayer = {
        key: "predefinedLEZs",
        translationKey: "layer.currentLowEmissionZones",
        isActive: true,
        opacity: 0.8,
        zIndex: 1,
        component: ({ opacity }: { opacity: number }) => (
          <PredefinedLEZsOnMap predefinedLEZs={deserialize(data)} opacity={opacity} />
        ),
        group: GROUPS.caseConfiguration,
        context: ["case-create"],
        layerManager: {
          showFrontLegend: true,
          showBackLegend: false,
          showInLayerManager: true,
        },
        legend: {
          legendType: "component",
          component: () => <PredefinedLEZsLegend />,
        },
      };
      return [predefinedLEZsLayer];
    },
  };
});

export const scenarioEmissionsAtom = (scenarioId?: string) =>
  atomWithQuery<WMSOverlayer[], unknown>(() => ({
    queryKey: ["scenarioEmissions", scenarioId],
    queryFn: async () => {
      if (!scenarioId) return [];
      const { data } = await axios.get<Emission[]>(`/planning-api/scenarios/${scenarioId}/emissions`);
      return data.map((emission) => ({
        key: `${emission.name}-${scenarioId}`,
        translationKey: `layer.scenario.${emission.type}`,
        url: "/geoserver/wms",
        layers: emission.type === "TRAFFIC" ? "atmoplan:traffic" : "atmoplan:pointsources",
        opacity: 0.8,
        context: ["case-results"],
        isActive: false,
        group: GROUPS.emissions,
        zIndex: 0,
        params: {
          layers: emission.type === "TRAFFIC" ? "atmoplan:traffic" : "atmoplan:pointsources",
          viewparams: `shapecollection_id:${emission.geoserverFilter}`,
          styles: emission.type === "TRAFFIC" ? "atmoplan:traffic_scenario" : "atmoplan:pointsources_scenario",
        },
        layerManager: {
          showFrontLegend: true,
          showBackLegend: false,
          showInLayerManager: true,
        },
        legend: {
          legendType: "wms",
        },
      }));
    },
  }));

const extraLayersAtom = atomWithQuery(() => ({
  queryKey: baseDataKeys.extraLayers,
  queryFn: async () => {
    const data = await fetchExtraLayers();
    return data.map(extraLayerToOverlayer);
  },
}));

const residentialMapsAtom = atomWithQuery(() => ({
  queryKey: baseDataKeys.residentialMaps,
  queryFn: async () => {
    const data = await queryResidentialMaps();
    return [
      // first total residential maps
      ...data.totalMapList.map(residentialTotalMapToOverlayer),

      // second residential maps with substance, fuel, dwelling and appliance subdivision
      ...data.mapList.map(residentialMapToOverlayer),
    ];
  },
}));

export const activeOverlayersAtom = atom<Array<Overlayer | ComponentOverlayer>>(filterActive(staticOverlayers));

// add or update overlayer
export const addOrUpdateActiveOverlayerAtom = atom(null, (get: Getter, set: Setter, update: Overlayer) => {
  set(activeOverlayersAtom, addOrUpdateInList(get(activeOverlayersAtom), update));
});

// active readio means all other overlayers from the same group are disabled
export const updateActiveRadioOverlayerAtom = atom(null, (get: Getter, set: Setter, update: Overlayer) => {
  const activeOverlayers = get(activeOverlayersAtom);
  const filtered = activeOverlayers.filter((overlayer) => overlayer.group.name !== update.group.name);
  const newActiveOverlayers = [...filtered, { ...update, isActive: true }];
  set(activeOverlayersAtom, newActiveOverlayers);
});

export const setLayerGroupToNoneAtom = atom(null, (get: Getter, set: Setter, group: LayerGroup) => {
  set(activeOverlayersAtom, () => {
    return get(activeOverlayersAtom).map((overlayer) => {
      if (overlayer.group.name === group.name) return { ...overlayer, isActive: false };
      return overlayer;
    });
  });
});
